import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { ArrowsLeftRight } from '@phosphor-icons/react';

export const SubstitutionModal = ({ 
  isOpen, 
  onClose, 
  team, 
  onSubstitute,
  subsRemaining = 3,
  mode = 'match' // 'match' (max 3-5 subs, in-game) | 'lineup' (unlimited, Teams page editor)
}) => {
  const [playerOut, setPlayerOut] = useState(null);
  const [playerIn, setPlayerIn] = useState(null);

  if (!team) return null;

  const lineupPlayers = team.lineup.map(num => team.players.find(p => p.number === num));
  const benchPlayers = team.bench.map(num => team.players.find(p => p.number === num));
  const isLineup = mode === 'lineup';

  const handleConfirm = () => {
    if (playerOut && playerIn) {
      onSubstitute(playerOut.number, playerIn.number);
      setPlayerOut(null);
      setPlayerIn(null);
      // In lineup-edit mode, keep modal open so the user can chain multiple
      // swaps without re-opening. In match mode, close immediately.
      if (!isLineup) onClose();
    }
  };

  const getPositionColor = (position) => {
    if (position === 'GK') return 'position-gk';
    if (['CB', 'LB', 'RB', 'LWB', 'RWB', 'SW'].includes(position)) return 'position-def';
    if (['CDM', 'CM', 'CAM', 'LM', 'RM'].includes(position)) return 'position-mid';
    return 'position-att';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121216] border-[#2C2C35] max-w-2xl" data-testid="substitution-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-white uppercase tracking-tight flex items-center gap-2">
            <ArrowsLeftRight size={20} className="text-[#007AFF]" />
            {isLineup ? 'Edit Starting XI' : 'Make Substitution'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between py-2 px-4 bg-[#0A0A0C] rounded-sm mb-4">
          <span className="text-sm text-[#A0A0A5]">
            {isLineup ? 'Swap a starter with a substitute — applies to every competition (League, Cup, Asian Cup, CWC, WC, Play Now).' : 'Substitutions remaining:'}
          </span>
          {!isLineup && <span className="font-mono font-bold text-white">{subsRemaining}/3</span>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Player Out (from lineup) */}
          <div>
            <h4 className="text-xs font-bold uppercase text-[#FF3B30] mb-2 tracking-wider">
              Player Out
            </h4>
            <ScrollArea className="h-[250px] border border-[#2C2C35] rounded-sm">
              <div className="p-2 space-y-1">
                {lineupPlayers.map(player => {
                  if (!player) return null;
                  const isSelected = playerOut?.id === player.id;
                  return (
                    <button
                      key={player.id}
                      data-testid={`sub-out-${player.number}`}
                      onClick={() => setPlayerOut(player)}
                      className={`
                        w-full text-left p-2 rounded-sm transition-all
                        ${isSelected ? 'bg-[#FF3B30]/20 border border-[#FF3B30]' : 'hover:bg-[#1A1A1F]'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-white w-5 text-sm">{player.number}</span>
                        <span className={`position-badge ${getPositionColor(player.position)}`}>
                          {player.position}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{player.name}</p>
                          <p className="text-[10px] text-[#6E6E73]">{player.flag} {player.age}y</p>
                        </div>
                        <span className="font-mono text-xs text-[#A0A0A5]">{player.overall}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Player In (from bench) */}
          <div>
            <h4 className="text-xs font-bold uppercase text-[#32D74B] mb-2 tracking-wider">
              Player In
            </h4>
            <ScrollArea className="h-[250px] border border-[#2C2C35] rounded-sm">
              <div className="p-2 space-y-1">
                {benchPlayers.map(player => {
                  if (!player) return null;
                  const isSelected = playerIn?.id === player.id;
                  return (
                    <button
                      key={player.id}
                      data-testid={`sub-in-${player.number}`}
                      onClick={() => setPlayerIn(player)}
                      className={`
                        w-full text-left p-2 rounded-sm transition-all
                        ${isSelected ? 'bg-[#32D74B]/20 border border-[#32D74B]' : 'hover:bg-[#1A1A1F]'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-white w-5 text-sm">{player.number}</span>
                        <span className={`position-badge ${getPositionColor(player.position)}`}>
                          {player.position}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white truncate">{player.name}</p>
                          <p className="text-[10px] text-[#6E6E73]">{player.flag} {player.age}y</p>
                        </div>
                        <span className="font-mono text-xs text-[#A0A0A5]">{player.overall}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Summary */}
        {(playerOut || playerIn) && (
          <div className="mt-4 p-3 bg-[#0A0A0C] rounded-sm border border-[#2C2C35]">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <p className="text-xs text-[#FF3B30] uppercase mb-1">Out</p>
                <p className="font-medium text-white text-sm">
                  {playerOut ? `#${playerOut.number} ${playerOut.name}` : '---'}
                </p>
              </div>
              <ArrowsLeftRight size={24} className="text-[#6E6E73]" />
              <div className="text-center">
                <p className="text-xs text-[#32D74B] uppercase mb-1">In</p>
                <p className="font-medium text-white text-sm">
                  {playerIn ? `#${playerIn.number} ${playerIn.name}` : '---'}
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          {!isLineup && (
            <Button
              variant="outline"
              onClick={onClose}
              className="bg-transparent border-[#2C2C35] text-white hover:bg-white hover:text-black"
              data-testid="sub-cancel-btn"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={!playerOut || !playerIn || (!isLineup && subsRemaining === 0)}
            className="bg-[#007AFF] hover:bg-[#0A84FF] text-white"
            data-testid="sub-confirm-btn"
          >
            {isLineup ? 'Swap' : 'Confirm Substitution'}
          </Button>
          {isLineup && (
            <Button
              onClick={onClose}
              className="bg-[#32D74B] hover:bg-[#32D74B]/80 text-black font-bold"
              data-testid="sub-done-btn"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
